import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { ChevronLeft, Save, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import StrategySetSection from './StrategySetSection';
import AdvancedSettingsSection from './AdvancedSettingsSection';
import MarketplaceSettingsSection from './MarketplaceSettingsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StrategyBuilder = ({ strategy, onSave, onCancel }) => {
  const [name, setName] = useState(strategy?.name || 'Untitled Strategy');
  const [description, setDescription] = useState(strategy?.description || '');
  const [strategySet, setStrategySet] = useState(strategy?.strategy_set || {
    entry: { description: '', conditions_text: '' },
    entry_positions: [],
    repair: null,
    repair_positions: [],
    overnight_protection: null,
    overnight_positions: [],
    universal_exit: null
  });
  const [advancedSettings, setAdvancedSettings] = useState(strategy?.advanced_settings || {});
  const [marketplaceSettings, setMarketplaceSettings] = useState(strategy?.marketplace_settings || {});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMarketplace, setShowMarketplace] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        name,
        description,
        strategy_set: strategySet,
        advanced_settings: advancedSettings,
        marketplace_settings: marketplaceSettings
      };

      if (strategy?.id) {
        await axios.put(`${API}/strategies/${strategy.id}`, data, { withCredentials: true });
        toast.success('Strategy updated successfully');
      } else {
        await axios.post(`${API}/strategies`, data, { withCredentials: true });
        toast.success('Strategy created successfully');
      }
      onSave();
    } catch (error) {
      console.error('Failed to save strategy:', error);
      toast.error('Failed to save strategy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="strategy-builder">
      {/* Header */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4 flex-1">
            <Button onClick={onCancel} variant="ghost" size="sm" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex-1 max-w-md">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Strategy Name"
                className="text-lg font-semibold"
                data-testid="strategy-name-input"
              />
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-strategy-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Strategy'}
          </Button>
        </div>
        <div className="mt-4">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Strategy description (optional)"
            rows={2}
            data-testid="strategy-description-input"
          />
        </div>
      </Card>

      {/* Strategy Set Section */}
      <StrategySetSection strategySet={strategySet} setStrategySet={setStrategySet} />

      {/* Advanced Settings */}
      <Card className="bg-white/80 backdrop-blur-sm border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-6 flex justify-between items-center hover:bg-slate-50 transition-colors"
          data-testid="toggle-advanced-settings"
        >
          <h3 className="text-xl font-bold text-indigo-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Advanced Settings
          </h3>
          {showAdvanced ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        {showAdvanced && (
          <div className="px-6 pb-6">
            <AdvancedSettingsSection settings={advancedSettings} setSettings={setAdvancedSettings} />
          </div>
        )}
      </Card>

      {/* Marketplace Settings */}
      <Card className="bg-white/80 backdrop-blur-sm border-gray-200 overflow-hidden">
        <button
          onClick={() => setShowMarketplace(!showMarketplace)}
          className="w-full p-6 flex justify-between items-center hover:bg-slate-50 transition-colors"
          data-testid="toggle-marketplace-settings"
        >
          <h3 className="text-xl font-bold text-indigo-700" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Marketplace Settings
          </h3>
          {showMarketplace ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </button>
        {showMarketplace && (
          <div className="px-6 pb-6">
            <MarketplaceSettingsSection settings={marketplaceSettings} setSettings={setMarketplaceSettings} />
          </div>
        )}
      </Card>
    </div>
  );
};

export default StrategyBuilder;
